/**
 * Module/layer table shared by table-driven apps (SafeCurves, NSW RLGSP): each
 * layer entry carries its map style, query properties, attributes layout,
 * table tab and legend tweaks, and the AppConfig pieces below derive from it.
 */

import type { Logger } from '@logtape/logtape';
import type { AttributesLayoutHelper } from '#lib/components/Attributes.types.js';
import type { FeatureRow, TableColumnDef } from '#lib/components/table/tanstackFeatures.js';
import type { SymbologyConfig } from '#lib/components/map/legend/symbology.js';
import type { TableTabConfig } from '#lib/apps/types.js';
import type { ILayerSource } from '#lib/types.js';

// A layer-style JSON from `layer_styles/`: a MapLibre layer fragment (`type`,
// zoom range, `layout`, `paint`, optional feature `filter`). JSON imports widen
// `type` to `string`, so consumers narrow it.
interface LayerStyle {
	type: string;
	minzoom?: number;
	maxzoom?: number;
	layout?: unknown;
	paint?: unknown;
	filter?: unknown;
}

interface TableTab {
	tabId: string;
	label: string;
	rowLabel: string;
	columns: TableColumnDef<FeatureRow>[];
}

export interface LayerEntry {
	// GeoServer feature title this layer draws from; defaults to the layer key.
	// Layers sharing a root feature share one vector source.
	featureTitle?: string;
	map: {
		style: LayerStyle;
		zIndex: number;
		// False excludes the layer from click-to-select and its hover cursor.
		selectable?: boolean;
	};
	// Dataset only published for some areas: no warning, legend row or table tab when missing.
	optional?: boolean;
	// Drawn from on-device data as an app-filled GeoJSON source (no GeoServer feature); `abstract` is its info text.
	local?: { abstract: string };
	attributes: AttributesLayoutHelper;
	// Present = this layer gets a Table-view tab; `columns` defaults to the attributes table.
	table?: Omit<TableTab, 'columns'> & { columns?: TableTab['columns'] };
	legend?: {
		// Start checked/visible on load; layers default to hidden.
		initiallyVisible?: boolean;
		// Render as one checkbox cell in a module-level grid instead of a full row.
		grid?: { column: string; row: string };
		// No row of its own: extra symbology toggled by the named sibling's row.
		partOf?: string;
		// Contribute no symbology items (a `partOf` layer restyling the primary's classes).
		hideSymbology?: boolean;
		symbology?: SymbologyConfig;
	};
}

export interface ModuleEntry {
	// Section heading in the app legend.
	label: string;
	// Module-level tab spanning the module's layers without a `table` of their own.
	table?: TableTab;
	layers: Record<string, LayerEntry>;
}

type ModuleTable = Record<string, ModuleEntry>;
type SystemLayers = Record<string, Record<string, ILayerSource | null> | null>;

/** `layerId → attributes layout`, consumed by the attributes panel. */
export function attributesColumnsOf(table: ModuleTable): Record<string, AttributesLayoutHelper> {
	return Object.fromEntries(
		Object.values(table).flatMap((m) =>
			Object.entries(m.layers).map(([layerId, l]) => [layerId, l.attributes] as const)
		)
	);
}

/** Table-view tabs: one per module declaring a `table`, plus one per layer declaring its own. */
export function tableTabsOf<TSystem>(table: ModuleTable): TableTabConfig<TSystem>[] {
	return Object.entries(table).flatMap(([moduleId, m]) => {
		const moduleLayers = (systemData: TSystem) => (systemData as SystemLayers)?.[moduleId] ?? {};
		const moduleTab = m.table
			? [
					{
						id: m.table.tabId,
						label: m.table.label,
						rowLabel: m.table.rowLabel,
						columns: m.table.columns,
						getLayerId: (systemData: TSystem) =>
							Object.entries(moduleLayers(systemData))
								.filter(([layerId]) => !m.layers[layerId]?.table)
								.map(([, source]) => source?.layer?.id)
								.filter((id): id is string => !!id)
					}
				]
			: [];
		const layerTabs = Object.entries(m.layers).flatMap(([layerId, l]) =>
			l.table
				? [
						{
							id: l.table.tabId,
							label: l.table.label,
							rowLabel: l.table.rowLabel,
							// Attributes `cell` takes the raw value, not a CellContext, so only header/key carry over.
							columns:
								l.table.columns ??
								(l.attributes.table ?? []).map(({ accessorKey, header }) => ({
									accessorKey,
									header
								})),
							optional: l.optional,
							getLayerId: (systemData: TSystem) => moduleLayers(systemData)?.[layerId]?.layer?.id
						}
					]
				: []
		);
		return [...moduleTab, ...layerTabs];
	});
}

/**
 * Builds the `module → layerId → ILayerSource | null` envelope, matching each
 * entry to its module's feature by title.
 */
export function buildModuleLayers<F extends { title: string }>(
	table: ModuleTable,
	featuresByModule: Partial<Record<string, F[]>>,
	toSource: (layerId: string, feature: F | undefined, def: LayerEntry) => ILayerSource | null,
	log: Logger
): Record<string, Record<string, ILayerSource | null>> {
	const system: Record<string, Record<string, ILayerSource | null>> = {};
	for (const [moduleId, moduleDef] of Object.entries(table)) {
		const moduleFeatures = featuresByModule[moduleId] ?? [];
		const entry: Record<string, ILayerSource | null> = {};
		for (const [layerId, def] of Object.entries(moduleDef.layers)) {
			const featureTitle = def.featureTitle ?? layerId;
			const feature = moduleFeatures.find((l) => l.title === featureTitle);
			// An unmatched layer otherwise vanishes silently (no map layer, legend toggle no-ops).
			if (!feature && !def.optional && !def.local) {
				log.warn('No feature titled "{title}" in module {module}; available: {titles}', {
					title: featureTitle,
					module: moduleId,
					titles: moduleFeatures.map((l) => l.title)
				});
			}
			entry[layerId] = toSource(layerId, feature, def);
		}
		system[moduleId] = entry;
	}
	return system;
}
